import React from 'react';
import moment from 'moment';
import {
    AutocompleteInput,
    DateField,
    DateInput,
    Filter,
    ReferenceInput,
    SelectInput,
    TextField,
    ShowButton,
    Link,
    SelectArrayInput,
    FunctionField,
} from 'react-admin';
import CustomSearchInput from '../components/CustomSearchInput';
import { ChargeMethod, OrderStatus, OrderType } from '../constants';
import ChipField from '../components/ChipField';
import CityField from '../components/CityField';
import BoxitChipField from '../components/BoxitChipField';
import MoneyField from '../components/MoneyField';
import VolumeField from '../components/VolumeField';
import UserReferenceField from '../components/UserReferenceField';
import OrderPagination from './OrderPagination';
import humanize from '../util/humanize';
import { Chip } from '@material-ui/core';
import { TextInput } from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

const CustomerRentField = props => {
    const { record } = props;
    return record &&
        record.customerRent &&
        record.rent &&
        record.customerRent !== record.rent ? (
        <MoneyField
            source="customerRent"
            label="Customer Rent"
            showCurrency
            {...props}
        />
    ) : null;
};

const OrderFilter = props => {
    const { permissions = [] } = props;
    const isAdmin = permissions.indexOf('admin') !== -1;
    return (
        <Filter variant="outlined" {...props}>
            <CustomSearchInput alwaysOn />

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="type"
                choices={Object.keys(OrderType).map(c => ({
                    id: c,
                    name: humanize(c),
                }))}
                parse={value => value}
                format={value => {
                    if (!value) return [];
                    if (Array.isArray(value)) return value;
                    return [value];
                }}
            />

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="chargeMethod"
                label="Charge Method"
                choices={[
                    { id: ChargeMethod.cbm_rate, name: 'CBM' },
                    { id: ChargeMethod.lump_sum, name: 'Lump Sum' },
                ]}
                parse={value => value}
                format={value => {
                    if (!value) return [];
                    if (Array.isArray(value)) return value;
                    return [value];
                }}
            />

            <ReferenceInput
                source="assigneeId"
                reference="users"
                resource="users"
                label="Assignee"
                filter={
                    isAdmin
                        ? { userRoles: ['sales-staff'] }
                        : {
                              userRoles: ['sales-staff'],
                              countryId: props.countryId,
                          }
                }
                perPage={50}
            >
                <SelectArrayInput
                    style={{ minWidth: '10rem' }}
                    parse={value => value}
                    format={value => {
                        if (!value) return [];
                        if (Array.isArray(value)) return value;
                        return [value];
                    }}
                />
            </ReferenceInput>

            <ReferenceInput
                source="partnerId"
                reference="partners"
                resource="partners"
                label="Partner"
            >
                <SelectArrayInput
                    style={{ minWidth: '10rem' }}
                    parse={value => value}
                    format={value => {
                        if (!value) return [];
                        if (Array.isArray(value)) return value;
                        return [value];
                    }}
                />
            </ReferenceInput>

            <ReferenceInput
                source="countryId"
                reference="country"
                resource="country"
                label="Country"
            >
                <SelectInput style={{ minWidth: '10rem' }} />
            </ReferenceInput>

            <ReferenceInput
                source="cityId"
                reference="city"
                resource="city"
                label="City"
            >
                <SelectInput style={{ minWidth: '10rem' }} />
            </ReferenceInput>

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="status"
                choices={Object.keys(OrderStatus).map(c => ({
                    id: c,
                    name: c,
                }))}
                parse={value => value}
                format={value => {
                    if (!value) return [];
                    if (Array.isArray(value)) return value;
                    return [value];
                }}
            />

            <ReferenceInput
                source="userId"
                reference="users"
                resource="users"
                label="Customer"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={user => (user.name ? user.name : user.email)}
                />
            </ReferenceInput>
            <TextInput source="id" label="Order ID" />

            <DateInput
                source="createdAt[$gte]"
                label="Created From"
                parse={v => v && moment(v).startOf('day').format()}
            />
            <DateInput
                source="createdAt[$lte]"
                label="Created To"
                parse={v => v && moment(v).endOf('day').format()}
            />

            <DateInput
                source="date[$gte]"
                label="Date From"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />

            <DateInput
                source="date[$lte]"
                label="Date To"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />

            <DateInput
                source="renewalDate[$gte]"
                label="Renewal Date From"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />

            <DateInput
                source="renewalDate[$lte]"
                label="Renewal Date To"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />
        </Filter>
    );
};

// Custom field components with links
const PartnerField = ({ record }) => {
    if (!record || !record.partnerName) return null;
    return (
        <Link to={`/partners/${record.partnerId}/show`}>
            {record.partnerName}
        </Link>
    );
};

const CityNameField = ({ record }) => {
    if (!record || !record.cityName) return null;
    return <Link to={`/city/${record.cityId}/show`}>{record.cityName}</Link>;
};

const CustomerNameField = ({ record }) => {
    if (!record || !record.userName) return null;
    return <Link to={`/users/${record.userId}/show`}>{record.userName}</Link>;
};

const AssigneeField = ({ record }) => {
    if (!record || !record.assigneeId || !record.assigneeName) return null;
    return (
        <Link to={`/users/${record.assigneeId}/show`}>
            {record.assigneeName}
        </Link>
    );
};

const PhoneField = ({ record }) => {
    if (!record || !record.userPhone) return null;
    return <span>{record.userPhone}</span>;
};

const OrderList = ({ permissions = [], ...props }) => {
    // Get the current user's country ID from the store
    const countryId =
        props.countryId || (props.currentUser && props.currentUser.countryId);

    // Column configuration with default visibility and order
    const defaultColumns: ColumnConfig[] = [
        // Default visible columns (essential information)
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'createdAt', label: 'Created', visible: true, order: 2 },
        { id: 'partner', label: 'Partner', visible: true, order: 3 },
        { id: 'assignee', label: 'Assignee', visible: true, order: 4 },
        { id: 'customer', label: 'Customer', visible: true, order: 5 },
        { id: 'type', label: 'Type', visible: true, order: 6 },
        {
            id: 'chargeMethod',
            label: 'Charge Method',
            visible: true,
            order: 7,
        },
        { id: 'city', label: 'City', visible: true, order: 8 },
        { id: 'date', label: 'Date', visible: true, order: 9 },
        { id: 'status', label: 'Status', visible: true, order: 10 },
        { id: 'source', label: 'Source', visible: true, order: 11 },
        { id: 'actions', label: 'Actions', visible: true, order: 12 },

        // Additional fields from API response (initially hidden/optional)
        { id: 'reference', label: 'Reference', visible: false, order: 13 },
        { id: 'updatedAt', label: 'Updated', visible: false, order: 14 },
        { id: 'userEmail', label: 'Email', visible: false, order: 15 },
        { id: 'userPhone', label: 'Phone', visible: false, order: 16 },
        { id: 'countryName', label: 'Country', visible: false, order: 17 },
        { id: 'renewalDate', label: 'Renewal', visible: false, order: 18 },
        { id: 'currency', label: 'Currency', visible: false, order: 19 },
        { id: 'amount', label: 'Amount', visible: false, order: 20 },
        { id: 'months', label: 'Duration', visible: false, order: 21 },
        { id: 'qty', label: 'Items', visible: false, order: 22 },
        { id: 'actualQty', label: 'Actual Qty', visible: false, order: 23 },
        { id: 'currentQty', label: 'Live Items', visible: false, order: 24 },
        { id: 'size', label: 'Size', visible: false, order: 25 },
        { id: 'currentSize', label: 'Live Size', visible: false, order: 26 },
        {
            id: 'customerRent',
            label: 'Partner Rent',
            visible: false,
            order: 27,
        },
        { id: 'rent', label: 'Boxit Rent', visible: false, order: 28 },
        { id: 'currentRent', label: 'Live Rent', visible: false, order: 29 },
        { id: 'rate', label: 'Rate', visible: false, order: 30 },
    ];

    // Render a column based on its ID
    const renderColumn = columnId => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        label="Created"
                        showTime
                    />
                );
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'assignee':
                return (
                    <FunctionField
                        key={columnId}
                        label="Assignee"
                        render={record => <AssigneeField record={record} />}
                    />
                );
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => <CustomerNameField record={record} />}
                    />
                );
            case 'type':
                return (
                    <FunctionField
                        key={columnId}
                        label="Type"
                        render={record => {
                            return <Chip label={humanize(record.orderType)} />;
                        }}
                    />
                );
            case 'chargeMethod':
                return (
                    <FunctionField
                        key={columnId}
                        label="Charge Method"
                        render={record => {
                            if (record.chargeMethod === ChargeMethod.cbm_rate)
                                return 'CBM';
                            if (record.chargeMethod === ChargeMethod.lump_sum)
                                return 'Lump Sum';
                            return '-';
                        }}
                    />
                );
            case 'city':
                return (
                    <FunctionField
                        key={columnId}
                        label="City"
                        render={record => record.cityName}
                    />
                );
            case 'date':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="date"
                        label="Date"
                    />
                );
            case 'renewalDate':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="renewalDate"
                        label="Renewal"
                    />
                );
            case 'months':
                return (
                    <TextField
                        key={columnId}
                        source="months"
                        label="Duration"
                    />
                );
            case 'qty':
                return (
                    <TextField
                        key={columnId}
                        source="qty"
                        label="Items"
                        sortable={false}
                    />
                );
            case 'currentQty':
                return (
                    <TextField
                        key={columnId}
                        source="currentQty"
                        label="Live Items"
                        sortable={false}
                    />
                );
            case 'size':
                return (
                    <VolumeField
                        key={columnId}
                        source="size"
                        label="Size"
                        showUnit
                    />
                );
            case 'currentSize':
                return (
                    <VolumeField
                        key={columnId}
                        source="currentSize"
                        label="Live Size"
                        showUnit
                        sortable={false}
                    />
                );
            case 'customerRent':
                return (
                    <CustomerRentField key={columnId} label="Partner Rent" />
                );
            case 'rent':
                return (
                    <MoneyField
                        key={columnId}
                        source="rent"
                        label="Boxit Rent"
                        showCurrency
                    />
                );
            case 'currentRent':
                return (
                    <MoneyField
                        key={columnId}
                        source="currentRent"
                        label="Live Rent"
                        showCurrency
                        sortable={false}
                    />
                );
            case 'rate':
                return (
                    <MoneyField
                        key={columnId}
                        source="rate"
                        label="Rate"
                        showCurrency
                    />
                );
            case 'reference':
                return <TextField key={columnId} source="reference" />;
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="updatedAt"
                        label="Updated"
                        showTime
                    />
                );
            case 'userEmail':
                return (
                    <TextField
                        key={columnId}
                        source="userEmail"
                        label="Email"
                    />
                );
            case 'userPhone':
                return (
                    <TextField
                        key={columnId}
                        source="userPhone"
                        label="Phone"
                    />
                );
            case 'countryName':
                return (
                    <TextField
                        key={columnId}
                        source="countryName"
                        label="Country"
                    />
                );
            case 'currency':
                return <TextField key={columnId} source="currency" />;
            case 'amount':
                return (
                    <FunctionField
                        key={columnId}
                        label="Amount"
                        render={record => {
                            if (!record.amount) return '-';
                            return `${record.currency || ''} ${(
                                record.amount / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'actualQty':
                return (
                    <TextField
                        key={columnId}
                        source="actualQty"
                        label="Actual Qty"
                    />
                );
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="order"
                    />
                );
            case 'source':
                return <ChipField key={columnId} source="source" />;
            case 'actions':
                return (
                    props.hasShow && (
                        <ShowButton key={columnId} style={{ padding: 0 }} />
                    )
                );
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Orders',
        permissions,
        defaultColumns,
        storageKey: 'orderListColumns',
        renderColumn,
        bulkActionButtons: false,
        filtersComponent: (
            <OrderFilter permissions={permissions} countryId={countryId} />
        ),
        sort: { field: 'id', order: 'DESC' },
        paginationComponent: <OrderPagination />,
        perPage: 10,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'orders',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

OrderList.defaultProps = {
    resource: 'orders',
    location: { search: '' },
};

export default OrderList;
